import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useCommunityActivityLog } from '../../hooks/use-admin';
import {
  Activity,
  Search,
  Filter,
  UserPlus,
  UserMinus,
  Shield,
  Ban,
  MessageSquare,
  Settings,
  Crown,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn } from '../../lib/utils';

const activityTypeConfig = {
  community_update_member_role: {
    icon: Shield,
    color: 'text-violet-400 bg-violet-500/20',
    label: 'Role Change',
  },
  community_kick_member: {
    icon: UserMinus,
    color: 'text-yellow-400 bg-yellow-500/20',
    label: 'Member Kicked',
  },
  community_ban_member: {
    icon: Ban,
    color: 'text-red-400 bg-red-500/20',
    label: 'Member Banned',
  },
  community_unban_member: {
    icon: UserPlus,
    color: 'text-green-400 bg-green-500/20',
    label: 'Member Unbanned',
  },
  community_hide_discussion: {
    icon: EyeOff,
    color: 'text-orange-400 bg-orange-500/20',
    label: 'Discussion Hidden',
  },
  community_unhide_discussion: {
    icon: Eye,
    color: 'text-blue-400 bg-blue-500/20',
    label: 'Discussion Unhidden',
  },
  community_delete_discussion: {
    icon: Trash2,
    color: 'text-red-400 bg-red-500/20',
    label: 'Discussion Deleted',
  },
  community_update_settings: {
    icon: Settings,
    color: 'text-zinc-400 bg-zinc-500/20',
    label: 'Settings Updated',
  },
  community_transfer_ownership: {
    icon: Crown,
    color: 'text-amber-400 bg-amber-500/20',
    label: 'Ownership Transferred',
  },
  member_joined: {
    icon: UserPlus,
    color: 'text-green-400 bg-green-500/20',
    label: 'Member Joined',
  },
  discussion_created: {
    icon: MessageSquare,
    color: 'text-blue-400 bg-blue-500/20',
    label: 'Discussion Created',
  },
};

const getActivityConfig = (type) => {
  return (
    activityTypeConfig[type] || {
      icon: Activity,
      color: 'text-zinc-400 bg-zinc-500/20',
      label:
        type?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) ||
        'Activity',
    }
  );
};

export default function CommunityAdminActivity() {
  const { community } = useOutletContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: activityData, isLoading } = useCommunityActivityLog(
    community?.oid,
    {
      page,
      limit,
      type: activityFilter !== 'all' ? activityFilter : undefined,
      search: searchQuery || undefined,
    }
  );

  const activities = activityData?.activities || [];
  const totalPages = Math.ceil((activityData?.total || 0) / limit);

  // Filter client-side if backend doesn't support it
  const filteredActivities = activities.filter((activity) => {
    const matchesSearch =
      !searchQuery ||
      activity.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.metadata?.targetUsername
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesType =
      activityFilter === 'all' || activity.activityType === activityFilter;
    return matchesSearch && matchesType;
  });

  const formatActivityDescription = (activity) => {
    const { activityType, metadata } = activity;

    switch (activityType) {
      case 'community_update_member_role':
        return `Changed ${metadata?.targetUsername || 'member'}'s role to ${metadata?.newRole
          }`;
      case 'community_kick_member':
        return `Kicked ${metadata?.targetUsername || 'a member'
          } from the community`;
      case 'community_ban_member':
        return `Banned ${metadata?.targetUsername || 'a member'
          } from the community`;
      case 'community_unban_member':
        return `Unbanned ${metadata?.targetUsername || 'a member'}`;
      case 'community_hide_discussion':
        return `Hid discussion: "${metadata?.discussionTitle || 'Unknown'}"`;
      case 'community_unhide_discussion':
        return `Unhid discussion: "${metadata?.discussionTitle || 'Unknown'}"`;
      case 'community_delete_discussion':
        return `Deleted discussion: "${metadata?.discussionTitle || 'Unknown'
          }"`;
      case 'community_update_settings':
        return `Updated community settings`;
      case 'community_transfer_ownership':
        return `Transferred ownership to ${metadata?.newOwnerUsername || 'another admin'
          }`;
      default:
        return activity.description || 'Unknown activity';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Activity Log</h1>
        <p className="text-zinc-400">
          Track all administrative actions in your community
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search activity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
            <Select value={activityFilter} onValueChange={setActivityFilter}>
              <SelectTrigger className="w-full md:w-48 bg-zinc-800 border-zinc-700 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem
                  value="all"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  All Activities
                </SelectItem>
                <SelectItem
                  value="community_update_member_role"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Role Changes
                </SelectItem>
                <SelectItem
                  value="community_kick_member"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Kicks
                </SelectItem>
                <SelectItem
                  value="community_ban_member"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Bans
                </SelectItem>
                <SelectItem
                  value="community_hide_discussion"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Hidden Discussions
                </SelectItem>
                <SelectItem
                  value="community_delete_discussion"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Deleted Discussions
                </SelectItem>
                <SelectItem
                  value="community_update_settings"
                  className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                >
                  Settings Changes
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Activity List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Activity History
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {activityData?.total || filteredActivities.length} total activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">No activity found</p>
              <p className="text-sm text-zinc-500 mt-1">
                {searchQuery || activityFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Activity will appear here as actions are taken'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredActivities.map((activity, index) => {
                const config = getActivityConfig(activity.activityType);
                const Icon = config.icon;

                return (
                  <div
                    key={activity._id || index}
                    className="flex items-start gap-4 p-4 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <div
                      className={cn('p-2 rounded-lg shrink-0', config.color)}
                    >
                      <Icon className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5 rounded-full',
                            config.color
                          )}
                        >
                          {config.label}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-200">
                        {formatActivityDescription(activity)}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                        <span>
                          By {activity.metadata?.actorUsername || 'Unknown'}
                        </span>
                        <span>•</span>
                        <span>
                          {new Date(activity.createdAt).toLocaleString()}
                        </span>
                      </div>
                      {activity.metadata?.reason && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Reason: {activity.metadata.reason}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-zinc-400">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
